import Vector from 'ol/source/Vector.js'
import Cluster from 'ol/source/Cluster.js'
import GeoJSON from 'ol/format/GeoJSON.js'
import { transformExtent } from 'ol/proj'
import { Fill, Style, Circle, Text, Icon } from 'ol/style'
import { scaleLinear } from 'd3-scale'
import getMarkerMap from '../components/MarkerMap/MarkerMap'
import { API_URL } from '../const'
import { AppCategory } from '../types'
import type { Extent } from 'ol/extent';

const format = new GeoJSON()

function normalizeExtent(extent: Extent, projection: string): Extent {
  //transform extent to EPSG:4326 if needed
  if (projection !== 'EPSG:4326') {
    extent = transformExtent(extent, projection, 'EPSG:4326')
  }
  // Normalizza le longitudes (x) nell’intervallo [-180, 180]
  const normalized = [
    ((extent[0] + 180) % 360) - 180,
    extent[1],
    ((extent[2] + 180) % 360) - 180,
    extent[3]
  ];
  //transform back to original projection if needed
  return projection !== 'EPSG:4326' ? transformExtent(normalized, 'EPSG:4326', projection) : normalized
}

export function getFeatureInfo(feature: any): number {
  const subFeatures = feature.get('features')
  return subFeatures.length === 1
    ? subFeatures[0].getProperties().ids
    : subFeatures.reduce((acc: any, subFeature: any) => {
      return acc + subFeature.getProperties().ids
    }, 0)
}

export const vectorSource = new Vector({
  format: new GeoJSON(),
  loader: (extent, resolution, projection, success, failure) => {
    const epsg4326Extent = transformExtent(extent, projection, 'EPSG:4326')
    //rount coordinates to avoid too many different requests to 5 decimal places
    const baseUrl = `${API_URL}/cluster-monuments/`
    const currentFilters = vectorSource.get('filters')
    const setLoading = vectorSource.get('setLoading')
    const params = new URLSearchParams(currentFilters).toString()

    const url = `${baseUrl}?bbox=${epsg4326Extent.join(
      ','
    )}&resolution=${resolution}&${params}`

    const xhr = new XMLHttpRequest()
    setLoading(true)
    vectorSource.set('currentUrl', url)

    xhr.open('GET', url)
    const onError = function () {
      setLoading(false)
      vectorSource.removeLoadedExtent(extent)
    }
    xhr.onerror = onError
    xhr.onload = function () {
      setLoading(false)
      if (xhr.status == 200) {
        const features = format.readFeatures(xhr.responseText)
        if (xhr.responseURL === vectorSource.get('currentUrl')) {
          vectorSource.clear()
          vectorSource.addFeatures(features)
          success && success(features)
          vectorSource.set('currentUrl', undefined)
          
        }
      } else {
        onError()
        if (xhr.responseURL === vectorSource.get('currentUrl')) {
          vectorSource.set('currentUrl', undefined)
          vectorSource.set('lastResolution', resolution)
        }
        console.error('error loading json', xhr.status, xhr.statusText)
      }
    }
    xhr.send()
  },

  strategy: (extent, resolution, projection) => {
    
    const savedExtents = vectorSource.get('savedExtents') as string | null
    const lastResolution = vectorSource.get('lastResolution') as number | null

    let loadedExtents = {} as {[key: string]: number}
    if(!savedExtents){
      loadedExtents = {}
    } else {
      try {
      loadedExtents = JSON.parse(savedExtents)
      } catch(e) {
        loadedExtents = {}
      }
    }
    const extentKey = JSON.stringify(extent)
    
    if(loadedExtents[extentKey] && loadedExtents[extentKey] != resolution){
      // extent already loaded at different resolution, remove it to reload
      vectorSource.removeLoadedExtent(extent)
    }
    if(lastResolution !== resolution){
      const keys = Object.keys(loadedExtents)
      for(const key of keys){
        if(loadedExtents[key] !== resolution){
          vectorSource.removeLoadedExtent(JSON.parse(key))
          delete loadedExtents[key]
        }
      }
    }
    loadedExtents[extentKey] = resolution
    vectorSource.set('savedExtents', JSON.stringify(loadedExtents))
    vectorSource.set('lastResolution', resolution)
    
    return [extent]
  },
})

export const clusterSource = new Cluster({
  distance: 0,
  source: vectorSource,
})

const clusterScale = scaleLinear().domain([9, 45, 46]).range([14, 24, 24])

const styleCache = {} as any
const iconsCache = {} as any

export function getFeatureStyle(feature: any) {

  const categories = vectorSource.get('categories') as AppCategory[]
  const info = getFeatureInfo(feature)
  let singleCluster = false
  if (info === 1) {
    const subFeatures = feature.getProperties().features
    const sproperties = subFeatures[0].getProperties()
    singleCluster = !!sproperties.cluster
  }

  if (info === 1 && !singleCluster) {
    const properties = feature.getProperties().features[0].getProperties()

    const appCategory = feature
      .getProperties()
      .features[0].getProperties().app_category
    const src = getMarkerMap({
      monument: {
        pictures_count: properties.pictures_count,
        in_contest: properties.in_contest,
        app_category: properties.app_category,
      },
      category: categories?.find((category) => category.id === appCategory),
    })
    try {
      const cachedIcon = iconsCache[src || 'no-image']
      if (cachedIcon) {
        return cachedIcon
      }

      const image = new Icon({
        anchor: [0.5, 1],
        anchorXUnits: 'fraction',
        anchorYUnits: 'fraction',
        src,
        width: 30,
      })
      const iconStyle = new Style({
        image,
      })
      iconsCache[src || 'no-image'] = iconStyle
      return iconStyle
    } catch (e) {

      throw new Error('Error loading image')
    }
  }

  const size = info.toString() as any
  let style = styleCache[size]
  if (!style) {
    style = new Style({
      image: new Circle({
        radius: clusterScale(info),
        fill: new Fill({
          color: '#3399CC',
        }),
      }),
      text: new Text({
        text: size.toString(),
        fill: new Fill({
          color: '#fff',
        }),
      }),
    })
    styleCache[size] = style
  }
  return style
}
